import React, {
  ButtonHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Images } from "../../assets";
import { useAppDispatch } from "../../redux/store";
import { userActions } from "../../redux/user/user.slice";
import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { ThemeType } from "../../theme/Theme";
import { AtiraToast } from "../../utils/AtiraToast";
import { Button } from "../Button";
import { AtiraImage } from "../Image";
import { FontSize } from "../Text";
import { LoginWithGoogleReturnPath } from "../../model/user/types/LoginWithGoogleReturnPath.enum";
import { User } from "../../model/user/User";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  padding?: string;
  fontSize?: keyof typeof Spacing;
  borderRadius?: keyof typeof Rounded;
  border?: string;
  children?: React.ReactNode;
  margin?: string;
  width?: string;
  height?: string;
  loading?: boolean;
  loadingColor?: keyof ThemeType;
}

const StyledButton: React.FC<ButtonProps> = styled(Button)`
  background-color: ${(props) =>
    props.backgroundColor
      ? props.theme[props.backgroundColor]
      : props.theme.white};
  color: ${(props) =>
    props.color ? props.theme[props.color] : props.theme.black};
  padding: ${(props) => props.padding || `${Spacing.s} ${Spacing.l}`};
  border-radius: ${(props) => props.borderRadius || Rounded.md};
  border: ${(props) => props.border || "none"};
  font-size: ${(props) => props.fontSize || FontSize.m};
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "2.3rem"};
  margin: ${(props) => props.margin || "0"};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${Spacing.s};
`;

interface GoogleButtonProps {
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  padding?: string;
  fontSize?: keyof typeof Spacing;
  borderRadius?: keyof typeof Rounded;
  border?: string;
  children?: React.ReactNode;
  margin?: string;
  width?: string;
  height?: string;
  loading?: boolean;
  title?: string;
  onSuccess?: (user: User) => Promise<void>;
}

export const GoogleButtonWindow: React.FC<GoogleButtonProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { onSuccess } = props;

  const popupRef = useRef<Window | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const googleAuth = async () => {
    try {
      setIsLoading(true);
      const googleAuthUrl = await dispatch(
        userActions.createLoginWithGoogleLink({
          returnPath: LoginWithGoogleReturnPath.CALLBACK,
        }),
      ).unwrap();

      const popup = window.open(
        googleAuthUrl,
        "google-login",
        "width=900,height=600,left=200,top=200",
      );

      if (!popup) {
        AtiraToast.error(t("common.popupBlocked"));
        setIsLoading(false);
        return;
      }

      popupRef.current = popup;

      window.addEventListener("message", handlePopupMessage);

      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          setIsLoading(false);
        }
      }, 5000);
    } catch (error: any) {
      console.log(error);
      AtiraToast.apiError(error);
    }
  };

  const verifyAndLogUser = useCallback(
    async (code: string) => {
      try {
        const { user } = await dispatch(
          userActions.loginWithGoogleWindow({
            code,
            returnPath: LoginWithGoogleReturnPath.CALLBACK,
          }),
        ).unwrap();

        return user;
      } catch (error) {
        console.log(error);
        AtiraToast.error(t("common.wrong_contact_support"));
        setIsLoading(false);
      }
    },
    [dispatch, t],
  );

  const handlePopupMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === "GOOGLE_LOGIN_SUCCESS") {
        window.removeEventListener("message", handlePopupMessage);
        const { code } = event.data;
        const user = await verifyAndLogUser(code);
        popupRef.current?.close();

        if (user) {
          await onSuccess?.(user);
        }
        setIsLoading(false);
      }
    },
    [verifyAndLogUser, onSuccess],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("message", handlePopupMessage);
    };
  }, [handlePopupMessage]);

  return (
    <StyledButton
      loading={isLoading}
      loadingColor="main"
      onClick={googleAuth}
      {...props}
    >
      <AtiraImage src={Images.GoogleIcon} width="1.5rem" />
      {props.children}
    </StyledButton>
  );
};
